/** Connection state as shown in the UI. Written by AppController only. */
import { type ReadonlySignal, type Signal, signal } from '@preact/signals';
import type { ConnectionStatus } from '../net/types';

/**
 * Situations in which the app stops connecting and asks the player:
 *  - version: the server runs a newer protocol, the page must be reloaded;
 *  - replaced: the same player connected from another tab or device.
 */
export type FatalReason = 'version' | 'replaced';

/** Trying to connect this long without getting through: the game is likely out of reach. */
export const STUCK_AFTER_MS = 15_000;

export class ConnectionStore {
  private readonly statusSignal: Signal<ConnectionStatus> = signal('idle');
  private readonly fatalSignal: Signal<FatalReason | null> = signal(null);
  private readonly stuckSignal: Signal<boolean> = signal(false);
  private stuckTimer: ReturnType<typeof setTimeout> | null = null;

  readonly status: ReadonlySignal<ConnectionStatus> = this.statusSignal;
  readonly fatal: ReadonlySignal<FatalReason | null> = this.fatalSignal;
  /**
   * Trying for STUCK_AFTER_MS without getting through (the computer with the game asleep,
   * the phone off the home Wi-Fi): the player should hear about it, not watch a spinner.
   */
  readonly stuck: ReadonlySignal<boolean> = this.stuckSignal;

  setStatus(status: ConnectionStatus): void {
    this.statusSignal.value = status;
    if (status === 'connecting' || status === 'reconnecting') {
      this.stuckTimer ??= setTimeout(() => {
        this.stuckTimer = null;
        this.stuckSignal.value = true;
      }, STUCK_AFTER_MS);
      return;
    }
    if (this.stuckTimer !== null) clearTimeout(this.stuckTimer);
    this.stuckTimer = null;
    this.stuckSignal.value = false;
  }

  setFatal(reason: FatalReason | null): void {
    this.fatalSignal.value = reason;
  }
}
